import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useUserAuth } from '../context/UserAuthContext';
import { useLang } from '../context/LangContext';
import { useLayout } from '../context/LayoutContext';
import { useNotification } from '../context/NotificationContext';
import { useAdditionalInfoCheck } from '../hooks/useAdditionalInfoCheck';
import Appbar from '../components/Appbar';
import ResponsiveView from '../layout/ResponsiveView';
import LanguageSelector from './LanguageSelector';
import NotificationBell from './NotificationBell';
import AdditionalInfoModal from '../components/AdditionalInfoModal';
import LoginRequiredModal from '../components/LoginRequiredModal';
import LogoutConfirmModal from '../components/LogoutConfirmModal';

// 웹 버전 스타일
const WebContainer = styled.div<{ $marginBottom?: string; $transparent?: boolean; $transform?: string }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 1240px;
  padding: 20px 0;
  gap: 20px;
  height: 90px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: ${({ $transparent }) => $transparent ? "transparent" : "#fff"};
  z-index: 1000;
  transform: ${({ $transform }) => $transform || 'translateX(0)'};
`;

const WebContent = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  /* padding: 0 24px; */
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  /* 1200px 이하일 때는 왼쪽 정렬로 변경 */
  @media (max-width: 1200px) {
    margin: 0;
    margin-left: 0;
  }
  
  /* 스크롤바 너비만큼 왼쪽으로 이동하여 보정 */
  @media (min-width: 1201px) {
    margin-left: calc(50vw - 600px);
    margin-right: auto;
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 0 1 auto;
  min-width: 0;
`;

const Logo = styled.img`
  width: 120px;
  height: auto;
  cursor: pointer;
  flex-shrink: 0;
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  width: 550px;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 42px;
  border: 1px solid #b3b3b3;
  border-radius: 12px;
  padding: 0 0px 0 16px;
  font-size: 14px;
  outline: none;
  
  &::placeholder {
    color: #999;
  }
  
  &:focus {
    border-color: #007bff;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 0px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    background: url('/common/search.svg') no-repeat center;
    background-size: contain;
  }
`;

const RightContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 0 0 auto;
`;

const NavigationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
`;

const NavItem = styled.button<{ $isActive?: boolean }>`
  background: none;
  border: none;
  font-size: 16px;
  font-weight: ${({ $isActive }) => $isActive ? '700' : '400'};
  color: ${({ $isActive }) => $isActive ? '#262626' : '#666'};
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
    color: #007bff;
  }
`;

const AuthContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const AuthText = styled.span`
  font-size: 12px;
  color: #666;
  white-space: nowrap;
`;

const LangContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const LangText = styled.span`
  font-size: 12px;
  color: #666;
  white-space: nowrap;
`;

const NotificationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const NotificationText = styled.span`
  font-size: 12px;
  color: #666;
  white-space: nowrap;
`;

const LayoutButton = styled.button`
  background: none;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f5f5f5;
    border-color: #007bff;
    color: #007bff;
  }
`;

const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const LoginButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoginIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const MobileLogo = styled.img`
  width: 80px;
  height: auto;
  cursor: pointer;
`;

const MobileTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const MobileRightContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// 모바일 전체 컨테이너
const MobileContainer = styled.div<{ $transparent?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: #fff;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
`;

// 앱바 래퍼 (60px 고정)
const MobileAppbarWrapper = styled.div`
  height: 60px;
  flex-shrink: 0;
`;

// 모바일 검색 컨테이너 (별도 줄에 배치)
const MobileSearchContainer = styled.div`
  padding: 0px 16px 4px 16px; /* 상단 패딩을 0으로, 하단 패딩 줄임 */
  margin-bottom: 8px;
  flex-shrink: 0;
`;

const MobileSearchInput = styled.input`
  width: 100%;
  height: 36px;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 0 36px 0 12px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  background-color: #fff;
  cursor: pointer;
  
  &::placeholder {
    color: #6c757d;
  }
  
  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    cursor: text;
  }
`;

const MobileSearchIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    width: 20px;
    height: 20px;
    background: url('/common/search.svg') no-repeat center;
    background-size: contain;
  }
  
  &:hover::before {
    opacity: 0.7;
  }
`;

const MobileSearchWrapper = styled.div`
  position: relative;
  width: 100%;
`;

interface ProjectAppbarProps {
  title?: string; // 모바일에서 사용할 타이틀
  currentPage?: 'home' | 'auction' | 'favorites' | 'mypage'; // 현재 페이지 (네비게이션 활성화용)
  showBackButton?: boolean; // 모바일에서 백버튼 표시 여부
  showLogo?: boolean; // 모바일에서 로고 표시 여부 (홈페이지용)
  showMobileSearch?: boolean; // 모바일에서 검색창 표시 여부
  onBack?: () => void; // 백버튼 클릭 콜백
  $marginBottom?: string;
  $transparent?: boolean;
}

const ProjectAppbar: React.FC<ProjectAppbarProps> = ({
  title,
  currentPage = 'home',
  showBackButton = false,
  showLogo = false,
  showMobileSearch = false,
  onBack,
  $marginBottom,
  $transparent,
}) => {
  const navigate = useNavigate();
  const { isLoggedIn, logout } = useUserAuth();
  const { lang } = useLang();
  const { toggleLayout } = useLayout();
  const { hasUnreadNotification, markNotificationAsRead } = useNotification();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scrollX, setScrollX] = useState(0);
  const mobileContainerRef = useRef<HTMLDivElement>(null);

  // 추가 정보 체크 훅들
  const auctionInfoCheck = useAdditionalInfoCheck({
    onNavigate: () => navigate('/auction')
  });

  const favoritesInfoCheck = useAdditionalInfoCheck({
    onNavigate: () => navigate('/favorites')
  });

  const notificationInfoCheck = useAdditionalInfoCheck({
    onNavigate: () => navigate('/notification')
  });

  // 좌우 스크롤 감지
  useEffect(() => {
    const handleScroll = () => {
      setScrollX(window.scrollX);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 간단한 번역 함수
  const t = (key: string, defaultKo: string, defaultEn?: string) => {
    const translations = {
      'nav.home': { ko: '홈', en: 'Home' },
      'nav.auction': { ko: '경매', en: 'Auction' },
      'nav.favorites': { ko: '즐겨찾기', en: 'Favorites' },
      'nav.mypage': { ko: '마이페이지', en: 'My Page' },
      'app.name': { ko: 'BidSpace', en: 'BidSpace' },
      'auth.login': { ko: '로그인', en: 'Login' },
      'auth.logout': { ko: '로그아웃', en: 'Logout' },
      'lang.current': { ko: '한국어', en: 'Eng' },
      'notification.bell': { ko: '알림', en: 'Alert' },
      'search.placeholder': { ko: '경매사, 경매, lot명으로 검색', en: 'Search by auction house, auction, lot name' },
    };
    
    const translation = translations[key as keyof typeof translations];
    if (translation) {
      return translation[lang];
    }
    return lang === 'en' ? (defaultEn || defaultKo) : defaultKo;
  };

  const handleNavigation = (path: string) => {
    // 로그인이 필요한 페이지들
    const loginRequiredPages = ['/auction', '/favorites'];
    
    if (!isLoggedIn && loginRequiredPages.includes(path)) {
      setShowLoginModal(true);
      return;
    }

    // 추가 정보 체크가 필요한 페이지들
    if (path === '/auction') {
      auctionInfoCheck.checkAndNavigate();
      return;
    }
    
    if (path === '/favorites') {
      favoritesInfoCheck.checkAndNavigate();
      return;
    }
    
    navigate(path);
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleLanguageChange = (language: 'ko' | 'en') => {
    console.log('Language changed to:', language);
    // 언어 변경 로직은 LanguageSelector 내부에서 처리됨
  };

  const handleNotificationClick = () => {
    console.log('Notification clicked');
    // 알림을 읽음으로 표시
    markNotificationAsRead();
    // 추가 정보 체크 후 알림 페이지로 이동
    notificationInfoCheck.checkAndNavigate();
  };

  const handleAuthClick = () => {
    if (isLoggedIn) {
      setShowLogoutModal(true);
    } else {
      navigate('/login');
    }
  };

  const handleBackClick = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1); // 기본적으로 뒤로가기
    }
  };

  const handleConfirmLogout = () => {
    logout();
    setShowLogoutModal(false);
  };

  const handleCloseLogoutModal = () => {
    setShowLogoutModal(false);
  };

  const handleCloseModal = () => {
    setShowLoginModal(false);
  };

  const handleLoginFromModal = () => {
    setShowLoginModal(false);
    navigate('/login');
  };

  const handleSearchClick = () => {
    // 모바일에서는 검색창 클릭 시 바로 검색 페이지로 이동
    navigate('/search');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      console.log('Search query:', searchQuery);
      // 검색어와 함께 검색 결과 페이지로 이동
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      // 검색어가 없으면 빈 검색 페이지로 이동
      navigate('/search');
    }
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  // 웹 버전 렌더링
  const renderWebView = () => (
    <WebContainer 
      $transparent={$transparent}
      $transform={`translateX(-${scrollX}px)`}
    >
      <WebContent>
        <TopRow>
          <LeftContainer>
            <Logo 
              src="/logo.png"
              alt="BidSpace Logo" 
              onClick={handleLogoClick}
            />
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder={t('search.placeholder', '작가명, 작품명으로 검색')}
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyPress={handleSearchKeyPress}
              />
              <SearchIcon onClick={handleSearchSubmit} />
            </SearchContainer>
          </LeftContainer>
          <RightContainer>
            {isLoggedIn ? (
              <>
                <NotificationContainer>
                  <NotificationBell 
                    onClick={handleNotificationClick} 
                    hasNotification={hasUnreadNotification}
                  />
                  <NotificationText>{t('notification.bell', '알림')}</NotificationText>
                </NotificationContainer>
                <AuthContainer>
                  <LoginButton onClick={handleAuthClick}>
                    <LoginIcon 
                      src="/common/logout.svg" 
                      alt="로그아웃"
                    />
                  </LoginButton>
                  <AuthText>{t('auth.logout', '로그아웃')}</AuthText>
                </AuthContainer>
              </>
            ) : (
              <AuthContainer>
                <LoginButton onClick={handleAuthClick}>
                  <LoginIcon 
                    src="/common/login.svg" 
                    alt="로그인"
                  />
                </LoginButton>
                <AuthText>{t('auth.login', '로그인')}</AuthText>
              </AuthContainer>
            )}
            <LangContainer>
              <LanguageSelector onLanguageChange={handleLanguageChange} />
              <LangText>{t('lang.current', '한국어')}</LangText>
            </LangContainer>
            {process.env.NODE_ENV === 'development' && (
              <LayoutContainer>
                <LayoutButton onClick={toggleLayout}>
                  레이아웃
                </LayoutButton>
              </LayoutContainer>
            )}
          </RightContainer>
        </TopRow>
        <NavigationContainer>
          <NavItem 
            $isActive={currentPage === 'home'}
            onClick={() => handleNavigation('/')}
          >
            {t('nav.home', '홈')}
          </NavItem>
          <NavItem 
            $isActive={currentPage === 'auction'}
            onClick={() => handleNavigation('/auction')}
          >
            {t('nav.auction', '경매')}
          </NavItem>
          <NavItem 
            $isActive={currentPage === 'favorites'}
            onClick={() => handleNavigation('/favorites')}
          >
            {t('nav.favorites', '즐겨찾기')}
          </NavItem>
          <NavItem 
            $isActive={currentPage === 'mypage'}
            onClick={() => handleNavigation('/mypage')}
          >
            {t('nav.mypage', '마이페이지')}
          </NavItem>
        </NavigationContainer>
      </WebContent>
    </WebContainer>
  );

  // 모바일 버전 렌더링
  const renderMobileView = () => (
    <MobileContainer $transparent={$transparent} ref={mobileContainerRef}>
      <MobileAppbarWrapper>
        <Appbar
          left={showLogo ? (
            <MobileLogo 
              src="/logo.svg" 
              alt="BidSpace Logo" 
              onClick={handleLogoClick}
            />
          ) : undefined}
          center={!showLogo && title ? <MobileTitle>{title}</MobileTitle> : undefined}
          right={
            <MobileRightContainer>
              <LanguageSelector onLanguageChange={handleLanguageChange} />
              {isLoggedIn && (
                <NotificationBell 
                  onClick={handleNotificationClick} 
                  hasNotification={hasUnreadNotification}
                />
              )}
            </MobileRightContainer>
          }
          showBackButton={showBackButton}
          onBack={showBackButton ? handleBackClick : undefined}
          $transparent={true}
          $height="60px"
        />
      </MobileAppbarWrapper>
      {showMobileSearch && (
        <MobileSearchContainer>
          <MobileSearchWrapper>
            <MobileSearchInput
              type="text"
              placeholder={t('search.placeholder', '작가명, 작품명으로 검색')}
              value={searchQuery}
              onChange={handleSearchChange}
              onClick={handleSearchClick}
              onKeyPress={handleSearchKeyPress}
            />
            <MobileSearchIcon onClick={handleSearchSubmit} />
          </MobileSearchWrapper>
        </MobileSearchContainer>
      )}
    </MobileContainer>
  );

  return (
    <>
      <ResponsiveView
        mobileView={renderMobileView()}
        desktopView={renderWebView()}
      />
      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={handleCloseModal}
        onLogin={handleLoginFromModal}
      />
      
      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onClose={handleCloseLogoutModal}
        onConfirm={handleConfirmLogout}
        language={lang}
      />

      {/* 경매 페이지 추가 정보 모달 */}
      <AdditionalInfoModal
        isOpen={auctionInfoCheck.showAdditionalInfoModal}
        onClose={auctionInfoCheck.handleCloseModal}
        onGoToAdditionalInfo={auctionInfoCheck.handleGoToAdditionalInfo}
      />

      {/* 즐겨찾기 페이지 추가 정보 모달 */}
      <AdditionalInfoModal
        isOpen={favoritesInfoCheck.showAdditionalInfoModal}
        onClose={favoritesInfoCheck.handleCloseModal}
        onGoToAdditionalInfo={favoritesInfoCheck.handleGoToAdditionalInfo}
      />

      {/* 알림 페이지 추가 정보 모달 */}
      <AdditionalInfoModal
        isOpen={notificationInfoCheck.showAdditionalInfoModal}
        onClose={notificationInfoCheck.handleCloseModal}
        onGoToAdditionalInfo={notificationInfoCheck.handleGoToAdditionalInfo}
      />
    </>
  );
};

export default ProjectAppbar;
